/**
 * Parse a date string from the backend as UTC.
 * The tenant backend sends naive UTC timestamps without a 'Z' suffix — append it so JS parses as
 * UTC. The central backend's timestamps carry their own offset (`+00:00`), which a trailing 'Z'
 * would make unparseable, so one is only added to a timestamp that has neither.
 */
export function parseUTCDate(dateStr: string): Date {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(dateStr)
  return new Date(hasZone ? dateStr : dateStr + 'Z')
}

const LONG_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }

/** A backend instant (a deadline, an expiry) as the calendar date it falls on in the viewer's zone. */
export function localDate(dateStr: string, options: Intl.DateTimeFormatOptions = LONG_DATE): string {
  return parseUTCDate(dateStr).toLocaleDateString(undefined, options)
}

/**
 * A backend calendar boundary defined in UTC — the free plan's packaging quota resets at 00:00 UTC
 * on the first of the month — as that same calendar date in every zone. Rendered in the viewer's
 * zone it would read a day early west of UTC, while the API's own refusal names the UTC date.
 */
export function utcCalendarDate(dateStr: string, options: Intl.DateTimeFormatOptions = LONG_DATE): string {
  return parseUTCDate(dateStr).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' })
}

/** How long ago something happened, as the mock renders it (`6 min ago`, `2 days ago`). */
export function relTime(dateStr: string | null): string {
  if (!dateStr) return '—'
  const min = Math.round((Date.now() - parseUTCDate(dateStr).getTime()) / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const h = Math.round(min / 60)
  if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`
  const d = Math.round(h / 24)
  if (d < 30) return `${d} day${d > 1 ? 's' : ''} ago`
  const mo = Math.round(d / 30)
  return `${mo} month${mo > 1 ? 's' : ''} ago`
}

/**
 * An absolute timestamp in the viewer's time zone, with the zone named (`15 Jul 2026, 09:30 CEST`).
 * Every page shows times through this one function, so two of them never disagree.
 */
export function dateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return parseUTCDate(dateStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

/** How long something took, `12m` / `1h 5m`. Null when either end is missing, so a caller can
 *  leave the slot out rather than print a placeholder duration. */
export function elapsed(fromStr: string | null, toStr: string | null): string | null {
  if (!fromStr || !toStr) return null
  const min = Math.round((parseUTCDate(toStr).getTime() - parseUTCDate(fromStr).getTime()) / 60000)
  if (min < 0) return null
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m ? `${h}h ${m}m` : `${h}h`
}
